/**
 * Opening a photograph a phone or a tablet just took.
 *
 * `<img src=objectURL>` alone is not enough for this, and the ways it fails
 * are all invisible to the person holding the phone: an iPhone hands over a
 * HEIC no browser but Safari can decode, an Android file manager hands over a
 * JPEG whose `type` is the empty string or the made-up `image/jpg`, and a
 * gallery told to `accept` three exact MIME types greys out half the roll.
 * All three arrive at the same place — "that image could not be opened" —
 * which is true and useless.
 *
 * So: accept anything that looks like a picture, decode it by every route the
 * browser offers, and convert HEIC ourselves rather than blaming the file.
 */

/**
 * What the file picker offers.
 *
 * `image/*` is the part that matters — anything narrower hides photos on some
 * Android pickers — but iOS and a few desktop dialogues still want the HEIC
 * types spelled out, and the extensions cover pickers that match on those
 * rather than on a MIME type.
 */
export const IMAGE_ACCEPT =
  'image/*,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif,.avif';

const IMAGE_EXTENSIONS = [
  '.jpg', '.jpeg', '.jpe', '.jfif', '.png', '.webp', '.gif',
  '.bmp', '.heic', '.heif', '.avif', '.tif', '.tiff',
];

/**
 * Whether to even try. Deliberately generous: the decode below is the real
 * test, and a file that will not open says so in a message of its own. A
 * camera roll picture with no `type` at all is a normal thing to be handed.
 */
export function looksLikeImage(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  const name = file.name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Is this an Apple HEIC/HEIF, whatever the file claims to be?
 *
 * Read from the bytes rather than the name or the MIME type, because a photo
 * copied through a chat app arrives as `IMG_0001.jpg` with `image/jpeg` on it
 * and HEIC inside. The layout is ISO-BMFF: a four-byte length, `ftyp`, then
 * the brand.
 */
export async function isHeic(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  if (head.length < 12) return false;
  const ascii = (from: number, to: number): string =>
    String.fromCharCode(...head.subarray(from, to));
  if (ascii(4, 8) !== 'ftyp') return false;
  return ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'].includes(
    ascii(8, 12).toLowerCase(),
  );
}

/** Which step failed, so the message can say what to do about it. */
export type ImageFailure = 'heic' | 'decode';

export class ImageOpenError extends Error {
  readonly kind: ImageFailure;
  constructor(kind: ImageFailure) {
    super(kind);
    this.kind = kind;
  }
}

/**
 * Decode any picture the device is likely to produce into an `<img>` the
 * cropper can measure, draw and re-encode.
 *
 * Three routes, in order of how well they preserve the original:
 *  1. straight `<img>`, which every browser optimises and which applies EXIF
 *     rotation, so a portrait taken sideways is upright;
 *  2. `createImageBitmap`, which decodes some files `<img>` refuses;
 *  3. HEIC converted to JPEG in a chunk that is only downloaded when a HEIC
 *     actually turns up — around 3 MB of WebAssembly nobody with an Android
 *     phone should have to pay for.
 *
 * The caller owns the returned element and must call `release()` when done:
 * the object URL behind it stays alive until then.
 */
export async function openImage(
  file: File,
): Promise<{ image: HTMLImageElement; release: () => void }> {
  const heic = await isHeic(file).catch(() => false);

  // Safari decodes HEIC natively and does it better than we can, so it is
  // still worth trying the direct route first even when we know the format.
  if (!heic) {
    const direct = await fromBlob(file).catch(() => null);
    if (direct) return direct;

    const viaBitmap = await bitmapToJpeg(file)
      .then((jpeg) => fromBlob(jpeg))
      .catch(() => null);
    if (viaBitmap) return viaBitmap;

    throw new ImageOpenError('decode');
  }

  const native = await fromBlob(file).catch(() => null);
  if (native) return native;

  try {
    const { heicTo } = await import('heic-to');
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
    return await fromBlob(jpeg);
  } catch {
    throw new ImageOpenError('heic');
  }
}

/** An `<img>` that has finished loading, or a rejection. */
function fromBlob(blob: Blob): Promise<{ image: HTMLImageElement; release: () => void }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      // A decode that "succeeds" with no pixels is a failure that would
      // otherwise show up as an empty crop ring.
      if (image.naturalWidth === 0 || image.naturalHeight === 0) {
        URL.revokeObjectURL(url);
        reject(new Error('empty image'));
        return;
      }
      resolve({ image, release: () => URL.revokeObjectURL(url) });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode failed'));
    };
    image.src = url;
  });
}

/** Re-encode through `createImageBitmap`, for files `<img>` will not take. */
async function bitmapToJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
      'image/jpeg',
      0.92,
    );
  });
}
